import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslations } from '../../contexts/translation.context';
import { Typography, ConfirmModal } from '../../components';
import { FiltersSection, WithdrawalTable } from './components';
import { WithdrawalRequest, WithdrawalsFilters } from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import { transformWithdrawalData } from './utils';
import { staffApi } from '../../api/staff';
import { showPartnerSuccess, showError } from '../../utils/toast';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

const WithdrawalPage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.withdrawalRequests'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<WithdrawalsFilters>(DEFAULT_FILTERS);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    orderId: string | null;
    status: 'paid' | 'pending' | 'cancel' | null;
  }>({
    isOpen: false,
    orderId: null,
    status: null,
  });

  // Local state for debounced search input
  const [localSearch, setLocalSearch] = useState(filters.search);

  // Debounce for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Sync local search with filters when they change externally
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Prepare API params
  const apiParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.status !== 'all') {
      params.status = filters.status as 'paid' | 'pending' | 'cancel';
    }

    return params;
  }, [currentPage, pageSize, filters.search, filters.status]);

  // Load withdrawals when dependencies change
  useEffect(() => {
    const loadWithdrawals = async () => {
      setIsLoading(true);
      try {
        const response = await staffApi.getWithdrawalRequests(apiParams);

        if (response && response.objects && Array.isArray(response.objects)) {
          const transformedWithdrawals = response.objects.map(transformWithdrawalData);
          setWithdrawals(transformedWithdrawals);
          setTotalCount(response.count || 0);
        } else {
          setWithdrawals([]);
          setTotalCount(0);
        }
      } catch (error: any) {
        console.error('Failed to load withdrawals:', error);
        const errorMessage = error?.response?.data?.detail
          || error?.response?.data?.message
          || error?.message
          || t('withdrawal.errors.loadFailed');
        showError(errorMessage);
        setWithdrawals([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadWithdrawals();
  }, [apiParams, t]);

  // Handlers
  const handleFilterChange = useCallback((key: keyof WithdrawalsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((id: string, status: 'paid' | 'pending' | 'cancel') => {
    setConfirmModal({
      isOpen: true,
      orderId: id,
      status,
    });
  }, []);

  const performStatusChange = useCallback(async () => {
    if (!confirmModal.orderId || !confirmModal.status) return;

    setIsLoading(true);
    try {
      await staffApi.changeWithdrawalStatus(confirmModal.orderId, confirmModal.status);

      const successMessages = {
        paid: t('withdrawal.success.paid'),
        cancel: t('withdrawal.success.cancelled'),
        pending: t('withdrawal.success.pending')
      };

      showPartnerSuccess(successMessages[confirmModal.status]);

      // Reload data
      const response = await staffApi.getWithdrawalRequests(apiParams);
      if (response && response.objects) {
        const transformedWithdrawals = response.objects.map(transformWithdrawalData);
        setWithdrawals(transformedWithdrawals);
        setTotalCount(response.count || 0);
      }
    } catch (error: any) {
      console.error('Failed to change withdrawal status:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || t('withdrawal.errors.statusChangeFailed');
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [confirmModal.orderId, confirmModal.status, apiParams, t]);

  // Check if there are active filters
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.status !== 'all'
    );
  }, [filters]);

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content */}
      <div className="flex-1">
        <div className="bg-white">
          {/* Title */}
          <div className="py-[16px] px-[24px]">
            <Typography variant="h5" className="font-semibold text-gray-900">
              {t('withdrawal.title')}
            </Typography>
          </div>

          {/* Filters */}
          <FiltersSection
            hasActiveFilters={hasActiveFilters}
            filters={filters}
            localSearch={localSearch}
            onSearchChange={setLocalSearch}
            onFilterChange={handleFilterChange}
            onResetFilters={handleResetFilters}
          />

          {/* Table */}
          <div className="px-[24px] pb-[24px]">
            <WithdrawalTable
              withdrawals={withdrawals}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onStatusChange={handleStatusChange}
            />
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, orderId: null, status: null })}
        onConfirm={performStatusChange}
        title={
          confirmModal.status === 'paid'
            ? t('withdrawal.markAsPaid')
            : confirmModal.status === 'cancel'
            ? t('withdrawal.cancel')
            : t('withdrawal.view')
        }
        message={
          confirmModal.status === 'paid'
            ? t('withdrawal.confirm.paid')
            : confirmModal.status === 'cancel'
            ? t('withdrawal.confirm.cancel')
            : t('withdrawal.confirm.pending')
        }
        confirmText={t('global.confirm')}
        cancelText={t('global.cancel')}
        confirmColor={confirmModal.status === 'cancel' ? 'danger' : 'primary'}
        isLoading={isLoading}
      />
    </div>
  );
};

export default WithdrawalPage;
